import React from 'react';
import { motion } from 'framer-motion';
import { Typewriter } from 'react-simple-typewriter';
import { HiOutlineDownload, HiOutlineMail } from 'react-icons/hi';
import { FaGithub, FaLinkedin } from 'react-icons/fa';

const Hero = () => {
  return (
    <section
      id="hero"
      className="min-h-screen bg-gradient-to-br from-charcoal-900 via-navy-900 to-charcoal-800 flex items-center justify-center text-white overflow-hidden relative"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-beige-400/10 rounded-full blur-3xl -top-32 -left-32 sm:-top-48 sm:-left-48 animate-pulse"></div>
        <div className="absolute w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-navy-400/10 rounded-full blur-3xl -bottom-32 -right-32 sm:-bottom-48 sm:-right-48 animate-pulse delay-700"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 py-16 sm:py-20 md:py-0 z-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 sm:gap-10 md:gap-12 lg:gap-16">
          {/* Left Content */}
          <motion.div
            className="flex-1 text-center md:text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-block mb-3 sm:mb-4"
            >
              <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-beige-500/20 text-beige-200 rounded-full text-xs sm:text-sm font-medium border border-beige-400/30">
                Full-Stack Software Engineer
              </span>
            </motion.div>

            <motion.h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-primary mb-4 sm:mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Hi, I'm{' '}
              <span className="bg-gradient-to-r from-beige-300 via-beige-400 to-beige-500 bg-clip-text text-transparent">
                Wajiha Niazi
              </span>
            </motion.h1>

            <motion.div
              className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-light font-secondary text-gray-300 mb-3 sm:mb-4 md:mb-5 h-14 sm:h-16 md:h-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Typewriter
                words={[
                  'React, Node.js & TypeScript Engineer',
                  '500+ Production PRs Delivered',
                  'Open Source Contributor',
                  'Tech Mentor & Educator',
                  'Remote-Ready Full-Stack Engineer',
                ]}
                loop
                cursor
                cursorStyle="|"
                typeSpeed={70}
                deleteSpeed={50}
                delaySpeed={1500}
              />
            </motion.div>

            <motion.p
              className="text-sm sm:text-base md:text-lg text-gray-400 mb-6 sm:mb-8 max-w-2xl mx-auto md:mx-0 leading-relaxed px-2 sm:px-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              Full-stack software engineer focused on React, Node.js, and TypeScript, with 500+ production PRs
              delivered across startup products. Passionate about scalable software, mentorship, and
              creating more opportunities for women in technology.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center md:justify-start mb-6 sm:mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <a
                href="/WajihaNiazi.pdf"
                download="Wajiha-Niazi-Resume.pdf"
                className="group px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-beige-400 to-beige-500 text-navy-900 font-semibold rounded-lg shadow-lg hover:shadow-beige-400/50 transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105 text-sm sm:text-base"
              >
                <HiOutlineDownload className="text-lg sm:text-xl group-hover:animate-bounce" />
                Download Resume
              </a>
              <a
                href="#contact"
                className="px-6 py-3 sm:px-8 sm:py-4 bg-transparent border-2 border-beige-400 text-beige-200 font-semibold rounded-lg hover:bg-beige-400/10 transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105 text-sm sm:text-base"
              >
                <HiOutlineMail className="text-lg sm:text-xl" />
                Get In Touch
              </a>
            </motion.div>

            {/* Social Links */}
            <motion.div
              className="flex gap-3 sm:gap-4 justify-center md:justify-start"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <a
                href="https://github.com/WajihaNiazi"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 sm:p-3 bg-charcoal-700/50 rounded-lg hover:bg-beige-500/20 border border-charcoal-600 hover:border-beige-400 transition-all duration-300 hover:scale-110"
              >
                <FaGithub className="text-xl sm:text-2xl text-beige-200" />
              </a>
              <a
                href="https://linkedin.com/in/wajiha-niazi-9b4991200"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 sm:p-3 bg-charcoal-700/50 rounded-lg hover:bg-beige-500/20 border border-charcoal-600 hover:border-beige-400 transition-all duration-300 hover:scale-110"
              >
                <FaLinkedin className="text-xl sm:text-2xl text-beige-200" />
              </a>
              <a
                href="mailto:wajihaniazi22@gmail.com"
                className="p-2.5 sm:p-3 bg-charcoal-700/50 rounded-lg hover:bg-beige-500/20 border border-charcoal-600 hover:border-beige-400 transition-all duration-300 hover:scale-110"
              >
                <HiOutlineMail className="text-xl sm:text-2xl text-beige-200" />
              </a>
            </motion.div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            className="flex-1 flex justify-center md:justify-end"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg">
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-beige-400 to-beige-600 rounded-2xl sm:rounded-3xl blur-2xl opacity-20"
                animate={{
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: 'reverse',
                }}
              ></motion.div>
              <motion.img
                src="/IMG_5817.jpg"
                alt="Wajiha Niazi"
                className="relative w-full h-auto aspect-[3/4] object-cover rounded-2xl sm:rounded-3xl shadow-2xl border-2 sm:border-4 border-beige-400/30"
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: 'reverse',
                  ease: 'easeInOut',
                }}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 hidden sm:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-beige-400/50 rounded-full flex justify-center pt-2"
        >
          <div className="w-1 h-2 sm:h-3 bg-beige-400 rounded-full"></div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
